'use client';

import Image from 'next/image';
import { useEffect, useState, useMemo } from 'react';
import styles from './index.module.less';
import { SearchResult } from '..';
import { useIsMobile } from '@/hooks/useIsMobile';

/**
 * 提取 URL 的域名
 * @param url - 需要提取域名的 URL
 * @returns 提取后的域名
 */
const extractDomain = (url: string) => {
  if (!url) return '';
  try {
    const normalizedUrl = /^(https?:)?\/\//.test(url) ? url : `https://${url}`;
    const { hostname } = new URL(normalizedUrl);
    return hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
};

interface ReportResultCardProps {
  results: SearchResult[];
  isLocked: boolean;
  originalImage: string;
  onImageClick: (result: SearchResult) => void;
}

export default function ResultContent({ results, isLocked, originalImage, onImageClick }: ReportResultCardProps) {
  const [imageErrorMap, setImageErrorMap] = useState<Record<number, boolean>>({});
  const [faviconErrorMap, setFaviconErrorMap] = useState<Record<number, boolean>>({});
  const isMobile = useIsMobile();

  // 使用 useMemo 同步计算 renderResults，避免 useEffect 的异步延迟导致 CLS
  // 确保首次渲染时就有正确的数据，骨架屏和内容在同一帧完成切换
  const renderResults = useMemo(() => {
    if (isLocked) {
      return Array(isMobile ? 8 : 12).fill({});
    }
    return results || [];
  }, [results, isLocked, isMobile]);

  // faviconErrorMap 重置仍需要 useEffect，但这不影响布局
  useEffect(() => {
    setFaviconErrorMap({});
  }, [results, isLocked]);

  return (
    <div className={styles.container}>
      {/* 图片网格 */}
      <div className={styles.grid}>
        {renderResults?.map((result, index) => {
          const domain = extractDomain(result.url);
          const faviconSrc =
            !domain || faviconErrorMap[index]
              ? '/images/image-search/default-icon.png'
              : `https://www.google.com/s2/favicons?sz=64&domain=${encodeURIComponent(domain)}`;

          return (
            <div key={result.image || index} className={styles.cardWrapper}>
              {/* 图片容器 - 响应式正方形 */}
              <div className={styles.imageContainer} onClick={() => onImageClick(result)}>
                <div className={isLocked ? styles.blurOverlay : ''}>
                  <Image
                    src={
                      isLocked
                        ? originalImage
                        : imageErrorMap[index] || !result.image
                          ? '/images/image-search/image-failed.png'
                          : result.image
                    }
                    alt={domain || 'Image result'}
                    fill
                    className={styles.resultImage}
                    sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
                    onError={() => setImageErrorMap((prev) => ({ ...prev, [index]: true }))}
                  />
                </div>

                {/* 底部遮罩 */}
                <div className={styles.bottomBar}>
                  {/* 网站图标 */}
                  <div className={styles.faviconWrapper}>
                    <Image
                      src={isLocked ? '/images/image-search/default-icon.png' : faviconSrc}
                      alt="websiteIcon"
                      width={isLocked ? 20 : 16}
                      height={isLocked ? 20 : 16}
                      className={styles.favicon}
                      onError={() =>
                        setFaviconErrorMap((prev) => ({
                          ...prev,
                          [index]: true,
                        }))
                      }
                    />
                  </div>

                  {/* 网站名称 */}
                  <span className={styles.domainText}>{isLocked ? '********.com' : domain}</span>

                  {/* 外链图标 */}
                  {isLocked ? (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M5 6.66406C5 5.33798 5.52678 4.06621 6.46447 3.12853C7.40215 2.19085 8.67392 1.66406 10 1.66406C11.3261 1.66406 12.5979 2.19085 13.5355 3.12853C14.4732 4.06621 15 5.33798 15 6.66406H15.8333C16.2754 6.66406 16.6993 6.83966 17.0118 7.15222C17.3244 7.46478 17.5 7.8887 17.5 8.33073V16.6641C17.5 17.1061 17.3244 17.53 17.0118 17.8426C16.6993 18.1551 16.2754 18.3307 15.8333 18.3307H4.16667C3.72464 18.3307 3.30072 18.1551 2.98816 17.8426C2.67559 17.53 2.5 17.1061 2.5 16.6641V8.33073C2.5 7.8887 2.67559 7.46478 2.98816 7.15222C3.30072 6.83966 3.72464 6.66406 4.16667 6.66406H5ZM10 3.33073C10.8841 3.33073 11.7319 3.68192 12.357 4.30704C12.9821 4.93216 13.3333 5.78001 13.3333 6.66406H6.66667C6.66667 5.78001 7.01786 4.93216 7.64298 4.30704C8.2681 3.68192 9.11594 3.33073 10 3.33073ZM11.6667 11.6641C11.6667 11.9566 11.5896 12.244 11.4434 12.4974C11.2971 12.7507 11.0867 12.9611 10.8333 13.1074V14.1641C10.8333 14.3851 10.7455 14.597 10.5893 14.7533C10.433 14.9096 10.221 14.9974 10 14.9974C9.77899 14.9974 9.56702 14.9096 9.41074 14.7533C9.25446 14.597 9.16667 14.3851 9.16667 14.1641V13.1074C8.84895 12.9239 8.60065 12.6408 8.46026 12.3018C8.31987 11.9629 8.29524 11.5871 8.3902 11.2327C8.48515 10.8783 8.69439 10.5652 8.98544 10.3418C9.2765 10.1185 9.63312 9.99741 10 9.9974C10.442 9.9974 10.866 10.173 11.1785 10.4856C11.4911 10.7981 11.6667 11.222 11.6667 11.6641Z"
                        fill="white"
                      />
                    </svg>
                  ) : (
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M1 11L11 1M11 1H3.5M11 1V9.125"
                        stroke="white"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
